import React, { type ReactNode, useMemo, useState } from 'react';
import { Button, Alert, Spinner } from '@/components/ui';
import InfoTooltip from '../InfoTooltip';
import ParentWidget, { WidgetRenderProps } from './ParentWidget';
import {
  CHART_SIZE_STYLES,
  HOME_PIE_CHART_PX,
  type ChartSize,
} from './shared/barGraphUtils';

export interface PcrPieSlice {
  label: string;
  count: number;
  color: string;
  subSlices?: PcrPieSlice[];
}

export interface PcrPieChartData {
  slices: PcrPieSlice[];
}

export interface PcrPieChartWidgetProps {
  id: string;
  title: string;
  size?: ChartSize;
  data?: PcrPieChartData;
  redirect?: { path: string; queryParams?: Record<string, string | number | boolean> };
  metadata?: { loading?: boolean; error?: Error | null; lastUpdated?: Date | null };
  onRefresh?: () => void;
  customization?: { hidden?: boolean; order?: number; themeOverride?: Record<string, unknown> };
  showRedirectIndicator?: boolean;
  requiredPermission: string;
  emptyMessage?: string;
  headerExtra?: ReactNode;
}

export interface PieArcSegment {
  key: string;
  parentSliceKey: string;
  color: string;
  startAngle: number;
  endAngle: number;
}

export interface PieChartLabel {
  key: string;
  parentSliceKey: string;
  x: number;
  y: number;
  textAnchor: 'start' | 'middle' | 'end';
  lines: string[];
  fontSize: number;
}

export interface PieChartGeometry {
  cx: number;
  cy: number;
  radius: number;
  viewSize: number;
  arcs: PieArcSegment[];
  sliceLabels: PieChartLabel[];
  subLabels: PieChartLabel[];
}

const PIE_RADIUS = 90;
const PIE_LABEL_PADDING = 52;
const SLICE_LABEL_OFFSET = 10;
const SUB_LABEL_RADIUS_FACTOR = 0.58;

function pieSlicePath(cx: number, cy: number, radius: number, startAngle: number, endAngle: number): string {
  const x1 = cx + radius * Math.cos(startAngle);
  const y1 = cy + radius * Math.sin(startAngle);
  const x2 = cx + radius * Math.cos(endAngle);
  const y2 = cy + radius * Math.sin(endAngle);
  const delta = endAngle - startAngle;
  const largeArc = delta > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}

function textAnchorForAngle(angle: number): 'start' | 'middle' | 'end' {
  const cos = Math.cos(angle);
  if (cos > 0.25) return 'start';
  if (cos < -0.25) return 'end';
  return 'middle';
}

function compactSliceLabel(label: string): string {
  return label.replace(/^Service Area\s+/i, 'SA ');
}

function labelPosition(
  cx: number,
  cy: number,
  midAngle: number,
  distance: number,
): { x: number; y: number; textAnchor: 'start' | 'middle' | 'end' } {
  return {
    x: cx + distance * Math.cos(midAngle),
    y: cy + distance * Math.sin(midAngle),
    textAnchor: textAnchorForAngle(midAngle),
  };
}

function pushSubStatusLabels(
  subLabels: PieChartLabel[],
  slice: PcrPieSlice,
  sliceStart: number,
  sliceSweep: number,
  cx: number,
  cy: number,
  radius: number,
  subLabelFontSize: number,
) {
  const positiveSub = (slice.subSlices ?? []).filter((s) => s.count > 0);
  if (positiveSub.length === 0) return;

  const subTotal = positiveSub.reduce((sum, s) => sum + s.count, 0);
  let subAngle = sliceStart;

  positiveSub.forEach((sub) => {
    const subSweep = subTotal > 0 ? (sub.count / subTotal) * sliceSweep : 0;
    const subMid = subAngle + subSweep / 2;
    subAngle += subSweep;

    const subPct = slice.count > 0 ? (sub.count / slice.count) * 100 : 0;
    const pos = labelPosition(cx, cy, subMid, radius * SUB_LABEL_RADIUS_FACTOR);
    subLabels.push({
      key: `${slice.label}-${sub.label}`,
      parentSliceKey: slice.label,
      ...pos,
      lines: [`${sub.label} ${sub.count}`, `${subPct.toFixed(0)}%`],
      fontSize: subLabelFontSize,
    });
  });
}

/** Build SVG arc segments and callout labels for flat or nested pie slices. */
export function buildPieChartGeometry(
  slices: PcrPieSlice[],
  sliceLabelFontSize: number,
  subLabelFontSize: number,
): PieChartGeometry {
  const viewSize = HOME_PIE_CHART_PX + PIE_LABEL_PADDING * 2;
  const cx = viewSize / 2;
  const cy = viewSize / 2;
  const radius = PIE_RADIUS;
  const total = slices.reduce((sum, s) => sum + s.count, 0);

  if (total <= 0) {
    return { cx, cy, radius, viewSize, arcs: [], sliceLabels: [], subLabels: [] };
  }

  const arcs: PieArcSegment[] = [];
  const sliceLabels: PieChartLabel[] = [];
  const subLabels: PieChartLabel[] = [];

  const positiveSlices = slices.filter((s) => s.count > 0);
  const singleNonZero = positiveSlices.length === 1;

  if (singleNonZero) {
    const only = positiveSlices[0]!;
    const subSlices = only.subSlices?.filter((s) => s.count > 0) ?? [];
    const slicePct = (only.count / total) * 100;
    const fullStart = -Math.PI / 2;
    const fullEnd = (3 * Math.PI) / 2;
    const fullSweep = fullEnd - fullStart;

    if (subSlices.length <= 1) {
      arcs.push({
        key: only.label,
        parentSliceKey: only.label,
        color: only.color,
        startAngle: fullStart,
        endAngle: fullEnd,
      });
      pushSubStatusLabels(subLabels, only, fullStart, fullSweep, cx, cy, radius, subLabelFontSize);
    } else {
      let angle = fullStart;
      const subTotal = subSlices.reduce((sum, s) => sum + s.count, 0);
      subSlices.forEach((sub, index) => {
        const sweep = (sub.count / subTotal) * 2 * Math.PI;
        const start = angle;
        const end = angle + sweep;
        arcs.push({
          key: `${only.label}-${sub.label}-${index}`,
          parentSliceKey: only.label,
          color: sub.color,
          startAngle: start,
          endAngle: end,
        });
        angle = end;
      });
      pushSubStatusLabels(subLabels, only, fullStart, fullSweep, cx, cy, radius, subLabelFontSize);
    }

    const pos = labelPosition(cx, cy, 0, radius + SLICE_LABEL_OFFSET);
    sliceLabels.push({
      key: only.label,
      parentSliceKey: only.label,
      ...pos,
      lines: [compactSliceLabel(only.label), `${slicePct.toFixed(1)}%`],
      fontSize: sliceLabelFontSize,
    });
    return { cx, cy, radius, viewSize, arcs, sliceLabels, subLabels };
  }

  let angle = -Math.PI / 2;

  slices.forEach((slice, sliceIndex) => {
    if (slice.count <= 0) return;
    const sliceSweep = (slice.count / total) * 2 * Math.PI;
    const sliceStart = angle;
    const sliceEnd = angle + sliceSweep;
    const sliceMid = sliceStart + sliceSweep / 2;
    const slicePct = (slice.count / total) * 100;
    const subSlices = slice.subSlices?.filter((s) => s.count > 0) ?? [];

    const slicePos = labelPosition(cx, cy, sliceMid, radius + SLICE_LABEL_OFFSET);
    sliceLabels.push({
      key: slice.label,
      parentSliceKey: slice.label,
      ...slicePos,
      lines: [compactSliceLabel(slice.label), `${slicePct.toFixed(1)}%`],
      fontSize: sliceLabelFontSize,
    });

    if (subSlices.length > 0) {
      const subTotal = subSlices.reduce((sum, s) => sum + s.count, 0);
      let subAngle = sliceStart;
      subSlices.forEach((sub, subIndex) => {
        const subSweep = subTotal > 0 ? (sub.count / subTotal) * sliceSweep : 0;
        if (subSweep > 0) {
          arcs.push({
            key: `${slice.label}-${sub.label}-${sliceIndex}-${subIndex}`,
            parentSliceKey: slice.label,
            color: sub.color,
            startAngle: subAngle,
            endAngle: subAngle + subSweep,
          });
        }
        subAngle += subSweep;
      });
      pushSubStatusLabels(subLabels, slice, sliceStart, sliceSweep, cx, cy, radius, subLabelFontSize);
    } else {
      arcs.push({
        key: `${slice.label}-${sliceIndex}`,
        parentSliceKey: slice.label,
        color: slice.color,
        startAngle: sliceStart,
        endAngle: sliceEnd,
      });
    }

    angle = sliceEnd;
  });

  return { cx, cy, radius, viewSize, arcs, sliceLabels, subLabels };
}

/** @deprecated Use buildPieChartGeometry — kept for tests. */
export function buildPieArcs(
  slices: PcrPieSlice[],
  cx: number,
  cy: number,
  radius: number,
): PieArcSegment[] {
  void cx;
  void cy;
  void radius;
  return buildPieChartGeometry(slices, 13, 9).arcs;
}

interface InteractivePieChartProps {
  slices: PcrPieSlice[];
  total: number;
  geometry: PieChartGeometry;
}

function InteractivePieChart({ slices, total, geometry }: InteractivePieChartProps) {
  const [hoveredSliceKey, setHoveredSliceKey] = useState<string | null>(null);
  const { cx, cy, radius, viewSize, arcs, subLabels } = geometry;

  // Crop the viewBox to just the disc — the geometry reserves wide margins for callout
  // labels we no longer draw (a legend replaces them), so cropping lets the donut fill
  // its box instead of rendering tiny inside a mostly-empty square.
  const crop = (viewSize - HOME_PIE_CHART_PX) / 2;
  const innerR = radius * 0.56; // donut hole

  const hoveredSlice =
    hoveredSliceKey != null ? slices.find((s) => s.label === hoveredSliceKey) ?? null : null;

  // Match the bar graph's hover UX: a floating InfoTooltip (portaled → never clipped) showing
  // the hovered slice's breakdown, instead of drawing cramped labels inside the donut.
  const tooltipContent = hoveredSlice ? (
    <div>
      <div className="tw-mb-1 tw-font-semibold">
        {compactSliceLabel(hoveredSlice.label)} — {hoveredSlice.count.toLocaleString()}
        {total > 0 ? ` (${((hoveredSlice.count / total) * 100).toFixed(1)}%)` : ''}
      </div>
      {(hoveredSlice.subSlices ?? [])
        .filter((s) => s.count > 0)
        .map((sub) => (
          <div key={sub.label} className="tw-flex tw-justify-between tw-gap-3">
            <span>{sub.label}</span>
            <span className="tw-tabular-nums">
              {sub.count.toLocaleString()}
              {hoveredSlice.count > 0 ? ` (${((sub.count / hoveredSlice.count) * 100).toFixed(0)}%)` : ''}
            </span>
          </div>
        ))}
    </div>
  ) : (
    // Keep the tooltip mounted (like the bar graph) so moving between slices doesn't remount/flicker.
    <span style={{ visibility: 'hidden' }}> </span>
  );

  return (
    <InfoTooltip content={tooltipContent} id="pcr-pie-hover" placement="top" maxWidth="240px">
    <svg
      width={HOME_PIE_CHART_PX}
      height={HOME_PIE_CHART_PX}
      viewBox={`${crop} ${crop} ${HOME_PIE_CHART_PX} ${HOME_PIE_CHART_PX}`}
      className="tw-shrink-0 pcr-pie-chart-svg"
      role="img"
      aria-label={slices
        .filter((s) => s.count > 0)
        .map((s) => `${s.label} ${((s.count / total) * 100).toFixed(1)}%`)
        .join(', ')}
      onMouseLeave={() => setHoveredSliceKey(null)}
    >
      {arcs.map((arc) => {
        const isHovered = hoveredSliceKey === arc.parentSliceKey;
        const isFullCircle = Math.abs(arc.endAngle - arc.startAngle - 2 * Math.PI) < 0.0001;
        const commonProps = {
          fill: arc.color,
          stroke: '#fff',
          strokeWidth: isHovered ? 2 : 1,
          className: 'pcr-pie-arc',
          style: { cursor: subLabels.length > 0 ? 'pointer' : undefined, opacity: isHovered ? 1 : 0.92 },
          onMouseEnter: () => setHoveredSliceKey(arc.parentSliceKey),
        };

        if (isFullCircle) {
          return <circle key={arc.key} cx={cx} cy={cy} r={radius} {...commonProps} />;
        }
        return (
          <path
            key={arc.key}
            d={pieSlicePath(cx, cy, radius, arc.startAngle, arc.endAngle)}
            {...commonProps}
          />
        );
      })}
      {/* Donut hole + grand total in the center. */}
      <circle cx={cx} cy={cy} r={innerR} fill="#ffffff" />
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" className="pcr-pie-center">
        <tspan x={cx} dy="-0.12em" fontSize={24} fontWeight={700} fill="#0f172a">
          {total.toLocaleString()}
        </tspan>
        <tspan x={cx} dy="1.4em" fontSize={11} fontWeight={600} fill="#64748b" letterSpacing="0.5">
          TOTAL
        </tspan>
      </text>
    </svg>
    </InfoTooltip>
  );
}

function PcrPieChartWidgetInner({
  id,
  title,
  size = 'medium',
  data,
  redirect,
  metadata,
  onRefresh,
  customization,
  showRedirectIndicator = true,
  requiredPermission,
  emptyMessage = 'No PCR data for the current filters.',
  headerExtra,
}: PcrPieChartWidgetProps) {
  const chartStyles = CHART_SIZE_STYLES[size];
  const sliceLabelFontSize = size === 'small' ? 12 : 14;
  const subLabelFontSize = size === 'small' ? 8 : 9;

  const renderChart = (props: WidgetRenderProps<PcrPieChartData>) => {
    const { metadata: chartMetadata, helpers } = props;
    const slices = data?.slices ?? [];
    const total = slices.reduce((sum, s) => sum + s.count, 0);

    if (chartMetadata.loading) {
      return (
        <div className="tw-flex tw-justify-center tw-items-center tw-w-full pcr-pie-chart-frame">
          <Spinner animation="border" className="tw-text-primary" role="status">
            <span className="tw-sr-only">Loading...</span>
          </Spinner>
        </div>
      );
    }

    if (chartMetadata.error) {
      return (
        <Alert variant="danger" role="alert">
          Error loading data: {chartMetadata.error.message}
        </Alert>
      );
    }

    if (chartMetadata.isEmpty || total === 0) {
      return (
        <div className="tw-flex tw-items-center tw-justify-center tw-text-center tw-text-content-muted tw-w-full pcr-pie-chart-frame">
          <p className="tw-mb-0 tw-px-2">{emptyMessage}</p>
        </div>
      );
    }

    const geometry = buildPieChartGeometry(slices, sliceLabelFontSize, subLabelFontSize);

    const legendItems = slices
      .filter((s) => s.count > 0)
      .map((s) => ({
        label: compactSliceLabel(s.label),
        count: s.count,
        pct: total > 0 ? (s.count / total) * 100 : 0,
        color: s.color,
      }));

    return (
      <div className="tw-relative tw-w-full">
        {onRefresh ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="tw-absolute tw-top-0 tw-right-0 tw-py-0 tw-px-1"
            style={{ zIndex: 2, fontSize: chartStyles.labelFontSize }}
            onClick={(e) => {
              e.stopPropagation();
              helpers.refresh();
            }}
          >
            Refresh
          </Button>
        ) : null}
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-6 tw-gap-y-3 tw-w-full pcr-pie-chart-frame">
          <InteractivePieChart slices={slices} total={total} geometry={geometry} />
          <ul className="tw-m-0 tw-flex tw-min-w-[9rem] tw-max-w-[14rem] tw-flex-1 tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
            {legendItems.map((it) => (
              <li key={it.label} className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-leading-tight">
                <span
                  className="tw-inline-block tw-h-2.5 tw-w-2.5 tw-shrink-0 tw-rounded-sm"
                  style={{ backgroundColor: it.color }}
                  aria-hidden="true"
                />
                <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-content">{it.label}</span>
                <span className="tw-shrink-0 tw-font-semibold tw-tabular-nums tw-text-ink">
                  {it.count.toLocaleString()}
                </span>
                <span className="tw-w-9 tw-shrink-0 tw-text-right tw-tabular-nums tw-text-content-muted">
                  {it.pct.toFixed(0)}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const isEmpty = useMemo(
    () => (d: PcrPieChartData | undefined) => {
      const sum = d?.slices?.reduce((s, x) => s + x.count, 0) ?? 0;
      return sum === 0;
    },
    [],
  );

  return (
    <ParentWidget<PcrPieChartData>
      id={id}
      title={title}
      size={size}
      data={data}
      redirect={redirect}
      metadata={metadata}
      onRefresh={onRefresh}
      customization={customization}
      showRedirectIndicator={showRedirectIndicator}
      requiredPermission={requiredPermission}
      isEmpty={isEmpty}
      headerExtra={headerExtra}
    >
      {renderChart}
    </ParentWidget>
  );
}

export function PcrPieChartWidget(props: PcrPieChartWidgetProps) {
  return <PcrPieChartWidgetInner {...props} />;
}

export default PcrPieChartWidget;
